import { Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoTexto } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { formatarInteiro } from '@/lib/formato';
import { combinaComTermo } from '@/lib/texto';

import { leiturasQueFaltam } from './leitura-do-modulo';

/**
 * ESCOLHER VÁRIAS PERMISSÕES DE UMA VEZ.
 *
 * Pedido do dono (27/10/2026), com a imagem da tela: *"melhorar layout e
 * múltipla seleção de permissões"*.
 *
 * ## O que estava errado, e eram três coisas
 *
 * UMA DE CADA VEZ. O administrador tem 114 permissões; montar um papel assim
 * é 114 escolhas, 114 idas ao servidor e 114 linhas de trilha para um ato só.
 * Quem desistisse no meio deixava o papel pela metade.
 *
 * A LISTA COBRIA O PAINEL. Era um campo de seleção com busca, e o painel de
 * detalhe tem 400 px: a lista abria para cima e tapava "Arquivar papel" — o
 * botão continuava clicável por baixo. Aqui não há sobreposição nenhuma: a
 * escolha é uma lista no fluxo da página, e o que ela empurra, ela empurra.
 *
 * O TEXTO SAÍA CORTADO. "abastecimento.estornar — ESTORNAR OU ..." não diz o
 * que se está concedendo, e conceder sem saber o quê é o oposto do que uma
 * tela de governança existe para fazer. Aqui a frase quebra em linha e
 * aparece inteira.
 *
 * ## Agrupado por módulo, e não numa lista corrida
 *
 * Cento e catorze linhas em ordem alfabética misturam combustível, oficina e
 * estoque. Por módulo, quem monta um papel de almoxarifado olha um bloco e
 * ignora os outros — e o "todas" de cada bloco é o atalho que o caso real
 * pede, porque papel costuma ser "tudo do módulo X, e mais duas coisas".
 */

export interface PermissaoEscolhivel {
  readonly id: string;
  readonly codigo: string;
  readonly descricao: string;
  readonly modulo: string;
}

export function EscolhaDePermissoes({
  permissoes,
  todas,
  jaConcedidas,
  carregando,
  pendente,
  nomeDoModulo,
  aoConceder,
}: {
  /** As que o papel ainda NÃO tem: é o que a lista oferece. */
  readonly permissoes: readonly PermissaoEscolhivel[];
  /** O catálogo inteiro: é nele que a leitura do módulo é procurada. */
  readonly todas: readonly PermissaoEscolhivel[];
  /** Os códigos que o papel já tem: a leitura que ele possui não se cobra de novo. */
  readonly jaConcedidas: ReadonlySet<string>;
  readonly carregando?: string;
  readonly pendente: boolean;
  /** "08 · Abastecimento". Sem o nome, o número sozinho não diz nada. */
  readonly nomeDoModulo: (modulo: string) => string;
  readonly aoConceder: (ids: readonly string[], concluido: () => void) => void;
}) {
  const [termo, setTermo] = useState('');
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string>>(new Set());

  const visiveis = useMemo(
    () => permissoes.filter((p) => combinaComTermo([p.codigo, p.descricao], termo)),
    [permissoes, termo],
  );

  /* Por módulo, na ordem do número — que é a ordem do v6, a mesma do menu. */
  const porModulo = useMemo(() => {
    const mapa = new Map<string, PermissaoEscolhivel[]>();
    for (const p of visiveis) mapa.set(p.modulo, [...(mapa.get(p.modulo) ?? []), p]);
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [visiveis]);

  /* A escolha SÓ CONTA O QUE AINDA ESTÁ NA LISTA: concedida por outra aba, a
     permissão sai de `permissoes` e não pode ir no lote — senão o pedido
     carregaria id que a tela já não mostra. */
  const validas = useMemo(
    () => permissoes.filter((p) => escolhidas.has(p.id)).map((p) => p.id),
    [permissoes, escolhidas],
  );

  /*
   * A LEITURA DO MÓDULO VAI JUNTO, E A TELA DIZ QUE VAI (27/10/2026).
   *
   * O dono concedeu `abastecimento.registrar` e a pessoa entrou sem ver nada:
   * a tela pede a licença de LER o módulo, e ele tinha liberado só a ação.
   * Nada avisava. Agora a escolha carrega a leitura que falta — e ela aparece
   * escrita, não some dentro do lote: quem concede tem de saber o que
   * concedeu.
   */
  const escolhidasInteiras = useMemo(
    () => permissoes.filter((p) => escolhidas.has(p.id)),
    [permissoes, escolhidas],
  );
  const faltas = useMemo(
    () => leiturasQueFaltam(escolhidasInteiras, jaConcedidas, todas),
    [escolhidasInteiras, jaConcedidas, todas],
  );
  /** As leituras que o botão leva junto, além do que está marcado. */
  const aLevar = useMemo(
    () => faltas.map((f) => f.incluir).filter((p): p is PermissaoEscolhivel => p !== null),
    [faltas],
  );

  const alternar = (id: string) => {
    setEscolhidas((agora) => {
      const nova = new Set(agora);
      if (nova.has(id)) nova.delete(id);
      else nova.add(id);
      return nova;
    });
  };

  const alternarGrupo = (doGrupo: readonly PermissaoEscolhivel[]) => {
    setEscolhidas((agora) => {
      const nova = new Set(agora);
      const todasJa = doGrupo.every((p) => nova.has(p.id));
      for (const p of doGrupo) {
        if (todasJa) nova.delete(p.id);
        else nova.add(p.id);
      }
      return nova;
    });
  };

  if (carregando !== undefined) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <span className="txt-rotulo-campo">Conceder permissões</span>
        <ControleCarregando oQue={carregando} />
      </div>
    );
  }

  if (permissoes.length === 0) {
    return <p className="txt-apoio text-tinta-secundaria">Este papel já tem todas as permissões do catálogo.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <CampoTexto
        rotulo="Conceder permissões"
        opcional
        valor={termo}
        aoMudar={setTermo}
        placeholder="Código ou o que ela libera"
        ajuda={`${formatarInteiro(permissoes.length)} ${permissoes.length === 1 ? 'disponível' : 'disponíveis'}. Marque quantas quiser e conceda de uma vez.`}
      />

      {porModulo.length === 0 ? (
        <p className="txt-apoio text-tinta-secundaria">
          Nenhuma permissão disponível com esse termo. Procure pelo código, como <span className="codigo">abastecimento</span>.
        </p>
      ) : (
        /* A LISTA ROLA DENTRO DA PRÓPRIA CAIXA, e não empurra o painel: com
           114 linhas no fluxo, o botão de conceder ficaria a uma tela de
           distância do que acabou de ser marcado. O teto é em `vh` para a
           caixa crescer no painel expandido (decisão 34). */
        <ul className="flex max-h-[42vh] flex-col gap-3 overflow-auto rounded-card border border-borda-sutil bg-rebaixada p-3">
          {porModulo.map(([modulo, doGrupo]) => {
            const todasJa = doGrupo.every((p) => escolhidas.has(p.id));
            return (
              <li key={modulo} className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="txt-apoio font-semibold text-tinta-secundaria">{nomeDoModulo(modulo)}</span>
                  <button
                    type="button"
                    onClick={() => {
                      alternarGrupo(doGrupo);
                    }}
                    className="txt-apoio shrink-0 text-marca-texto"
                  >
                    {todasJa ? 'Nenhuma' : 'Todas'}
                  </button>
                </div>
                <ul className="flex flex-col gap-1">
                  {doGrupo.map((p) => {
                    const marcada = escolhidas.has(p.id);
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          aria-pressed={marcada}
                          onClick={() => {
                            alternar(p.id);
                          }}
                          /* Marcada = laranja 50 com contorno laranja 500, o
                             mesmo da linha selecionada e do item ativo do
                             menu (R02, p.31). */
                          className={`flex w-full items-start gap-2 rounded-controle border p-2 text-left transition-colors ${
                            marcada ? 'border-marca bg-marca-fundo' : 'border-transparent bg-card hover:border-borda'
                          }`}
                        >
                          <span
                            aria-hidden="true"
                            className={`mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-[var(--raio-1)] border ${
                              marcada ? 'border-marca bg-marca text-acao-rotulo' : 'border-borda-forte bg-card'
                            }`}
                          >
                            {marcada ? <Check className="size-3" strokeWidth={3} /> : null}
                          </span>
                          <span className="flex min-w-0 flex-col gap-0.5">
                            <span className="codigo txt-apoio text-tinta">{p.codigo}</span>
                            {/* A frase INTEIRA, quebrando: cortada, ela não diz
                                o que se está concedendo. */}
                            <span className="txt-apoio text-pretty text-tinta-secundaria">{p.descricao}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      {faltas.length === 0 ? null : (
        /* Banner no FLUXO da página (p.30), e não toast: ele não é retorno de
           ação — é uma condição do que está prestes a acontecer, e precisa
           estar visível na hora de decidir. */
        <div className="flex flex-col gap-1 rounded-card border border-borda bg-marca-fundo p-3">
          {faltas.map((f) => (
            <p key={f.acoes.join()} className="txt-apoio text-pretty text-tinta-secundaria">
              {f.incluir === null ? (
                <>
                  <span className="codigo text-tinta">{f.acoes.join(', ')}</span> não abre tela nenhuma sem uma licença de
                  leitura de {nomeDoModulo(todas.find((p) => p.codigo === f.acoes[0])?.modulo ?? '')}. Marque também a que
                  este papel precisa:{' '}
                  {f.escolher.map((l, i) => (
                    <span key={l.id}>
                      {i > 0 ? ', ' : ''}
                      <span className="codigo text-tinta">{l.codigo}</span>
                    </span>
                  ))}
                  .
                </>
              ) : (
                <>
                  Vai junto <span className="codigo text-tinta">{f.incluir.codigo}</span>: sem ela a pessoa pode{' '}
                  <span className="codigo text-tinta">{f.acoes.join(', ')}</span> e não vê a tela onde se faz isso.
                </>
              )}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {validas.length === 0 ? null : (
          <button
            type="button"
            onClick={() => {
              setEscolhidas(new Set());
            }}
            className="txt-botao mr-auto px-2 text-marca-texto"
          >
            Limpar
          </button>
        )}
        <Botao
          nivel="secundario"
          disabled={validas.length === 0 || pendente}
          onClick={() => {
            if (validas.length === 0) return;
            /* A leitura entra no MESMO lote, e não num pedido depois: duas
               idas deixariam o papel com a ação e sem a leitura se a segunda
               falhasse — que é exatamente o estado que se está consertando. */
            const comLeitura = [...new Set([...validas, ...aLevar.map((p) => p.id)])];
            aoConceder(comLeitura, () => {
              setEscolhidas(new Set());
              setTermo('');
            });
          }}
        >
          {validas.length === 0
            ? 'Conceder permissões'
            : `Conceder ${formatarInteiro(validas.length + aLevar.length)} ${validas.length + aLevar.length === 1 ? 'permissão' : 'permissões'}`}
        </Botao>
      </div>
    </div>
  );
}
